import { useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import PageTransition from '../components/PageTransition';
import { getSubscriptionPlans } from '../api/dbApi';
import { appname, applogo, slides, appnameonly } from "../variables";
import '../styles/home.css';
import Footer from './Footer';


export default function Home() {
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showPricingModal, setShowPricingModal] = useState(false);

  // --- FEATURE DATA ---
  const features = [
    { icon: 'bi-phone', title: 'Multi-Device', desc: 'Control multiple WhatsApp numbers' },
    { icon: 'bi-megaphone', title: 'Bulk Messaging', desc: 'Broadcast to thousands instantly' },
    { icon: 'bi-clock-history', title: 'Smart Scheduling', desc: 'Send messages at the right time' },
    { icon: 'bi-robot', title: 'Chatbot Automation', desc: 'Reply instantly, 24×7' },
    { icon: 'bi-graph-up-arrow', title: 'Analytics', desc: 'Track delivery & performance' },
    { icon: 'bi-shield-lock', title: 'Secure', desc: 'End-to-end encrypted sessions' }
  ];

  // --- AUTHENTIC LIVE DATA ENGINE ---
  const [featureIndex, setFeatureIndex] = useState(0);
  const [stats, setStats] = useState({
    users: 12400,
    devices: 8920,
    chats: 2400000,
    uptime: 99.98,
    latency: 142
  });
  const [logs, setLogs] = useState(["> Initializing secure node...", "> Cluster_STABLE"]);
  const [time, setTime] = useState(new Date().toLocaleTimeString());

  useEffect(() => {
    // 1. FAST LOOP: Messages & Time (Every 1 second)
    const fastLoop = setInterval(() => {
      setTime(new Date().toLocaleTimeString());
      setStats(prev => ({
        ...prev,
        chats: prev.chats + Math.floor(Math.random() * 7) + 1,
        latency: 120 + Math.floor(Math.random() * 40)
      }));
    }, 1000);

    // 2. FEATURE LOOP: Cycle features (Every 4 seconds)
    const featureLoop = setInterval(() => {
      setFeatureIndex((prev) => (prev + 1) % features.length);
    }, 2000);

    // 3. SLOW LOOP: Users, Devices & Uptime (Every 5 seconds)
    const slowLoop = setInterval(() => {
      setStats(prev => ({
        ...prev,
        users: prev.users + (Math.random() > 0.7 ? 1 : 0),
        devices: prev.devices + (Math.random() > 0.85 ? 1 : 0),
        uptime: parseFloat((99.96 + Math.random() * 0.03).toFixed(2))
      }));
    }, 5000);

    // 4. LOG LOOP: System Logs
    const logEvents = ["API_POST: /v1/send_msg", "AUTH: Token_Verified", "NODE: SYNC_COMPLETE", "DB: Entry_Stored", "WH: Payload_Sent", "SSL: Handshake_OK"];
    const logLoop = setInterval(() => {
      setLogs(prev => {
        const newLog = `> ${logEvents[Math.floor(Math.random() * logEvents.length)]} [${Math.random().toString(36).substring(7)}]`;
        return [...prev.slice(-3), newLog];
      });
    }, 3000);

    // 5. LOAD PLANS
    const loadPlans = async () => {
      try {
        const res = await getSubscriptionPlans(null);
        setPlans(res.data?.plans || []);
      } catch (e) { console.error(e); } finally { setLoading(false); }
    };
    loadPlans();

    return () => {
      clearInterval(fastLoop);
      clearInterval(featureLoop);
      clearInterval(slowLoop);
      clearInterval(logLoop);
    };
  }, []);

  return (
    <PageTransition>
      <div className="home-fixed-viewport">

        {/* TOP STATUS TICKER */}
        <div className="d-flex justify-content-between align-items-center mb-3 opacity-75">
          <div className="small d-flex gap-4 align-items-center">
            <span className="text-successnew"><span className="status-dot-pulse me-2"></span> SYSTEM ONLINE</span>
            <div className="vr opacity-25" style={{ height: '10px' }}></div>
            <motion.span key={stats.users} className="text-white stat-flash">
              <i className="bi bi-people-fill me-1"></i> {stats.users.toLocaleString()} Users
            </motion.span>
            <motion.span key={stats.chats} className="text-white stat-flash">
              <i className="bi bi-chat-left-dots-fill me-1"></i> {stats.chats.toLocaleString()} Messages
            </motion.span>
          </div>
          <div className="small text-white font-monospace">CURRENT TIME: {time}</div>
        </div>

        {/* HEADER */}
        <header className="d-flex justify-content-between align-items-center mb-4">
          <div className="d-flex align-items-center gap-2">
            {/* <img src={applogo} alt="logo" width="40" height="40" /> */}
            <img src={appnameonly} alt="logo" width="100%" height="40" />
            {/* <span className="fw-black fs-4 tracking-tighter">{appname}</span> */}
          </div>
          <div className="d-flex gap-3">
            <button className="btn btn-sm text-white-50 fw-bold" onClick={() => navigate('/login')}>LOG IN</button>
            <button className="btn btn-primary btn-sm rounded-pill px-4 fw-bold shadow-sm" onClick={() => navigate('/register')}>SIGN UP FREE</button>
          </div>
        </header>

        {/* BENTO DASHBOARD */}
        <div className="bento-container">

          {/* 1. HERO */}
          {/* 1. DYNAMIC HERO: The Command Center Slider */}
          <div className="bento-item hero-block border-0 bg-transparent ps-0">
            <AnimatePresence mode="wait">
              <motion.div
                key={featureIndex % slides.length}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.6, ease: "easeOut" }}
              >
                {/* Sub-header Badge with Dynamic Accent */}
                <div className="d-flex align-items-center gap-2 mb-3">
                  <span
                    className="badge rounded-pill bg-opacity-10 px-3 py-2 fw-bold"
                    style={{
                      backgroundColor: slides[featureIndex % slides.length].accent + '22',
                      color: slides[featureIndex % slides.length].accent,
                      border: `1px solid ${slides[featureIndex % slides.length].accent}44`
                    }}
                  >
                    <span className="status-dot-pulse me-2" style={{ background: slides[featureIndex % slides.length].accent }}></span>
                    SYSTEM_OBJECTIVE: {slides[featureIndex % slides.length].title.toUpperCase()}
                  </span>
                </div>

                <h1 className="display-2 fw-black mb-3" style={{ lineHeight: '1.1' }}>
                  {slides[featureIndex % slides.length].title.split(' ').slice(0, -1).join(' ')} <br />
                  <span
                    className="glow-text transition-all"
                    style={{ color: slides[featureIndex % slides.length].accent }}
                  >
                    {slides[featureIndex % slides.length].title.split(' ').pop()}.
                  </span>
                </h1>

                <p className="text-white-50 fs-5 mb-5 pe-lg-5" style={{ maxWidth: '600px' }}>
                  {slides[featureIndex % slides.length].desc}
                </p>
              </motion.div>
            </AnimatePresence>

            {/* Dynamic Buttons & Progress */}
            <div className="d-flex flex-wrap align-items-center gap-4">
              <div className="d-flex gap-3">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="btn btn-white btn-lg rounded-4 px-5 fw-black text-dark shadow-lg"
                  onClick={() => navigate('/register')}
                >
                  GET STARTED
                </motion.button>
                <button className="btn btn-outline-light btn-lg rounded-4 px-4 border-2" onClick={() => setShowPricingModal(true)}>
                  PRICING
                </button>

                
              </div>

              {/* Slide Pagination Indicators */}
              <div className="d-flex gap-2 ms-lg-2">
                {slides.map((_, i) => (
                  <div
                    key={i}
                    className="rounded-pill transition-all"
                    style={{
                      height: '6px',
                      width: (featureIndex % slides.length) === i ? '30px' : '6px',
                      background: (featureIndex % slides.length) === i ? slides[i].accent : 'rgba(255,255,255,0.2)'
                    }}
                  ></div>
                ))}
              </div>
            </div>

            {/* Background Dynamic Ambient Light */}
            <motion.div
              animate={{
                background: `radial-gradient(circle at 20% 50%, ${slides[featureIndex % slides.length].accent}11, transparent 70%)`
              }}
              className="position-absolute top-0 start-0 w-100 h-100"
              style={{ zIndex: -1, pointerEvents: 'none' }}
            />
          </div>

          {/* 2. LIVE PULSE */}
          <div className="bento-item pulse-block">
            <h6 className="text-white-50 x-small fw-bold text-uppercase mb-3">Infrastructure Pulse</h6>
            <div className="row g-2 mb-3">
              <div className="col-6">
                <div className="p-2 rounded bg-primary bg-opacity-10 border border-primary border-opacity-10">
                  <div className="x-small text-white-50">Active Devices</div>
                  <motion.div key={stats.devices} className="fw-bold stat-flash">{stats.devices.toLocaleString()}</motion.div>
                </div>
              </div>
              <div className="col-6">
                <div className="p-2 rounded bg-primary bg-opacity-10 border border-primary border-opacity-10">
                  <div className="x-small text-white-50">Latency (ms)</div>
                  <motion.div key={stats.latency} className="fw-bold stat-flash">{stats.latency}ms</motion.div>
                </div>
              </div>
            </div>
            <div className="mb-2">
              <div className="d-flex justify-content-between small mb-1">
                <span>Uptime Efficiency</span>
                <motion.span key={stats.uptime} className="text-successnew fw-bold">{stats.uptime}%</motion.span>
              </div>
              <div className="stat-bar bg-dark" style={{ height: 4 }}>
                <motion.div animate={{ width: `${stats.uptime}%` }} className="stat-progress bg-success" style={{ height: '100%' }}></motion.div>
              </div>
            </div>
          </div>

          {/* 3. DYNAMIC FEATURES (Replaces Node Instances) */}
          <div className="bento-item features-block d-flex flex-column justify-content-center">
            <h6 className="text-white-50 x-small fw-bold text-uppercase mb-3">Platform Features</h6>
            <AnimatePresence mode="wait">
              <motion.div
                key={featureIndex}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.5 }}
                className="d-flex align-items-start gap-3"
              >
                <div className="bg-primary bg-opacity-10 p-2 rounded-3">
                  <i className={`bi ${features[featureIndex].icon} text-primary fs-4`}></i>
                </div>
                <div>
                  <div className="fw-bold small">{features[featureIndex].title}</div>
                  <div className="extra-small text-white-50">{features[featureIndex].desc}</div>
                </div>
              </motion.div>
            </AnimatePresence>
            <div className="d-flex gap-1 mt-3">
              {features.map((_, i) => (
                <div key={i} className={`flex-fill`} style={{ height: '2px', background: i === featureIndex ? '#6366f1' : 'rgba(255,255,255,0.1)', transition: '0.3s' }}></div>
              ))}
            </div>


            <div className="px-1 mt-5">
              <AnimatePresence mode="wait">
                <motion.div
                  key={featureIndex}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="d-flex justify-content-between align-items-center"
                >
                  <span className="text-white-50 font-monospace" style={{ fontSize: '0.6rem' }}>
                    STATUS: <span className="text-success">READY</span>
                  </span>
                  <span className="text-primary fw-bold" style={{ fontSize: '0.65rem' }}>
                    {features[featureIndex].desc}
                  </span>
                </motion.div>
              </AnimatePresence>

              {/* Matching Progress Bar */}
              <div className="stat-bar bg-dark mt-2" style={{ height: 4 }}>
                <motion.div
                  animate={{ width: `${((featureIndex + 1) / features.length) * 100}%` }}
                  className="stat-progress bg-primary"
                  style={{ height: '100%' }}
                ></motion.div>
              </div>
            </div>
          </div>




          {/* 4. LIVE LOGS */}
          <div className="bento-item logs-block" style={{ background: '#020617' }}>
            <h6 className="text-success x-small fw-bold text-uppercase mb-2 font-monospace">Real-time Logs</h6>
            <div className="font-monospace overflow-hidden">
              <AnimatePresence mode="popLayout">
                {logs.map((log, i) => (
                  <motion.div
                    key={log + i}
                    initial={{ opacity: 0, x: -5 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, y: -5 }}
                    className="terminal-text text-success"
                  >
                    {log}
                  </motion.div>
                ))}
              </AnimatePresence>
            </div>
          </div>

          {/* 5. PRICING PREVIEW */}
          {/* 5. PRICING PREVIEW: The 2-Column Node Grid */}
          <div className="bento-item pricing-block">
  <div className="bento-card-header">
    <h6 className="text-white-50 x-small fw-bold text-uppercase m-0">Subscription Plans</h6>
  </div>

  <div className="bento-card-body mt-3">
    {loading ? (
      <div className="text-center py-3">
        <div className="spinner-border spinner-border-sm text-primary"></div>
      </div>
    ) : (
      <>
        {/* Responsive Grid: 2 columns on mobile (col-6), 3 on desktop (col-md-4) */}
        <div className="row g-2 mb-3">
          {plans.map((p, i) => {
            const isActive = i === (featureIndex % plans.length);

            return (
              <div className="col-6 col-md-4" key={p.id}>
                <motion.div
                  onClick={() => setShowPricingModal(true)}
                  animate={{
                    // When active, use solid primary color. When inactive, stay dark/transparent.
                    backgroundColor: isActive ? '#6366f1' : 'rgba(255, 255, 255, 0.03)',
                    borderColor: isActive ? '#818cf8' : 'rgba(255, 255, 255, 0.05)',
                    y: isActive ? -4 : 0,
                    scale: isActive ? 1.02 : 1
                  }}
                  className="p-2 rounded border h-100 position-relative overflow-hidden d-flex flex-column justify-content-between shadow-sm"
                  style={{ transition: 'all 0.4s ease', cursor: 'pointer' }}
                >
                  <div className="d-flex justify-content-between align-items-start">
                    <span 
                      className={`fw-black ${isActive ? 'text-white' : 'text-white-50'}`} 
                      style={{ fontSize: '0.7rem', letterSpacing: '0.5px' }}
                    >
                      {p.name.split(' ')[0].toUpperCase()}
                    </span>
                    {isActive && (
                      /* White pulse dot for better visibility on blue background */
                      <div className="status-dot-pulse bg-white" style={{ width: 6, height: 6 }}></div>
                    )}
                  </div>

                  <div className="mt-2">
                    <div 
                      className={`fw-black ${isActive ? 'text-white' : 'text-successnew'}`} 
                      style={{ fontSize: '1.1rem' }}
                    >
                      ₹{p.price}
                    </div>
                    <div 
                      className={`font-monospace ${isActive ? 'text-white opacity-75' : 'text-white-50'}`} 
                      style={{ fontSize: '0.55rem' }}
                    >
                      {p.device_limit} NODES / {p.duration_days}D
                    </div>
                  </div>
                  
                  {/* Subtle inner glow for the active primary card */}
                  {isActive && (
                    <div className="position-absolute top-0 start-0 w-100 h-100 opacity-25" 
                         style={{ background: 'radial-gradient(circle at top right, white, transparent)' }}>
                    </div>
                  )}
                </motion.div>
              </div>
            );
          })}
        </div>

        {/* Sync Footer */}
        <div className="px-1 mt-auto">
          <div className="d-flex justify-content-between align-items-center mb-1">
            <span className="text-white-50 font-monospace" style={{ fontSize: '0.6rem' }}>ACTIVE_MSG_QUOTA</span>
            <motion.span
              key={featureIndex}
              className="text-primary fw-bold"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              style={{ fontSize: '0.65rem' }}
            >
              {plans[featureIndex % plans.length]?.message_limit.toLocaleString()} MSG
            </motion.span>
          </div>

          <div className="d-flex gap-1" style={{ height: 4 }}>
            {plans.map((_, i) => (
              <motion.div
                key={i}
                className="flex-fill rounded-pill"
                animate={{
                  backgroundColor: i === (featureIndex % plans.length) ? '#6366f1' : 'rgba(255,255,255,0.1)',
                  opacity: i === (featureIndex % plans.length) ? 1 : 0.5
                }}
              />
            ))}
          </div>
        </div>
      </>
    )}
  </div>
</div>

          {/* 6. SECURITY */}
          {/* 6. TESTIMONIALS: Verified User Signals */}
          <div className="bento-item testimonials-block">
            <div className="bento-card-header">
              <h6 className="text-white-50 x-small fw-bold text-uppercase m-0">Testimonials</h6>
            </div>

            <div className="bento-card-body">
              <AnimatePresence mode="wait">
                <motion.div
                  key={featureIndex} // Syncs with your global loop
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 1.05 }}
                  className="h-100 d-flex flex-column justify-content-center"
                >
                  {/* Testimonial Content */}
                  <div className="mb-3">
                    <i className="bi bi-quote text-primary opacity-50 fs-4"></i>
                    <p className="fw-medium text-white mb-2" style={{ fontSize: '0.75rem', lineHeight: '1.4', fontStyle: 'italic' }}>
                      {[
                        "Best WhatsApp API wrapper I've used. Flawless scaling.",
                        "The chatbot automation saved us 40+ hours a week.",
                        "Security protocols are top-notch. Highly recommend.",
                        "Setup took less than 5 minutes. Incredible UX."
                      ][featureIndex % 4]}
                    </p>
                  </div>

                  {/* User Identity */}
                  <div className="d-flex align-items-center gap-2 mt-auto pt-2 border-top border-white border-opacity-5">
                    <div className="bg-primary bg-opacity-20 rounded-circle d-flex align-items-center justify-content-center" style={{ width: 24, height: 24 }}>
                      <i className="bi bi-person-fill text-primary" style={{ fontSize: '0.7rem' }}></i>
                    </div>
                    <div className="d-flex flex-column">
                      <span className="fw-bold text-white" style={{ fontSize: '0.65rem' }}>
                        {["Alex R.", "Sarah M.", "David K.", "Elena W."][featureIndex % 4]}
                      </span>
                      <span className="text-success font-monospace" style={{ fontSize: '0.5rem' }}>
                        VERIFIED_PARTNER
                      </span>
                    </div>
                  </div>
                </motion.div>
              </AnimatePresence>
            </div>
          </div>

          {/* 7. QUICK ACTION */}
          {/* 7. QUICK ACTION: The System Ignition Button */}
          <motion.div
            whileHover={{ scale: 1.02, boxShadow: '0 0 25px rgba(99, 102, 241, 0.4)' }}
            whileTap={{ scale: 0.98 }}
            className="bento-item launch-block bg-primary text-center d-flex flex-column justify-content-center border-0 overflow-hidden position-relative"
            style={{ cursor: 'pointer', background: 'linear-gradient(135deg, #6366f1 0%, #4338ca 100%)' }}
            onClick={() => navigate('/register')}
          >
            {/* Background Decorative "Scanning" Element */}
            <motion.div
              animate={{
                y: [-100, 200],
                opacity: [0, 0.3, 0]
              }}
              transition={{ repeat: Infinity, duration: 2, ease: "linear" }}
              className="position-absolute top-0 start-0 w-100 h-25"
              style={{ background: 'linear-gradient(180deg, rgba(255,255,255,0.4), transparent)' }}
            />

            <div className="position-relative z-1">
              <h5 className="fw-black mb-0 tracking-tighter" style={{ fontSize: '1.2rem' }}>LAUNCH</h5>

              {/* Dynamic Status Label */}
              <div className="d-flex align-items-center justify-content-center gap-1 mt-1">
                <motion.div
                  animate={{ opacity: [1, 0.5, 1] }}
                  className="bg-white rounded-circle"
                  style={{ width: 4, height: 4 }}
                />
                <span className="font-monospace text-white-50" style={{ fontSize: '0.55rem', letterSpacing: '1px' }}>
                  READY_TO_DEPLOY
                </span>
              </div>

              <i className="bi bi-arrow-right-circle fs-3 mt-2 d-block"></i>
            </div>

            {/* Corner Technical Detail */}
            <div className="position-absolute bottom-0 end-0 p-2 opacity-25">
              <i className="bi bi-terminal" style={{ fontSize: '2rem' }}></i>
            </div>
          </motion.div>

        </div>

        {/* FOOTER */}
        {/* <div className="d-flex flex-column flex-md-row justify-content-between align-items-center mt-3 pt-3 border-top border-white border-opacity-10">
        <div className="text-center text-md-start">
        <div className="fw-bold text-white mb-1">Indian Technology Company</div>
        <div className="small text-white-50 lh-sm">
          107A/1 Criper Road, Konnagar<br />
          Hooghly, 712235, India
        </div>
      </div>
          <div className="d-flex flex-wrap justify-content-center gap-3">
            <a href="/privacy" className="legal-pill">Privacy</a>
            <a href="/terms" className="legal-pill">Terms</a>
            <a href="/mandate" className="legal-pill">Mandate</a>
            <a href="/refund" className="legal-pill">Refund</a>
            <a href="/support" className="legal-pill">Support</a>
          </div>
          <div className="small text-white-50">
            © 2026 Indian Technology Company <span className="status-dot-pulse ms-2"></span>
          </div>
        </div> */}
        <Footer />  

      </div>

     {/* PRICING MODAL OVERLAY - FULL GRID VIEW */}
<AnimatePresence>
  {showPricingModal && (
    <div className="modal-overlay p-3 p-lg-5" onClick={() => setShowPricingModal(false)}>
      <motion.div 
        initial={{ opacity: 0, y: 50 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 50 }}
        className="plan-modal-content-wide container-fluid"
        onClick={(e) => e.stopPropagation()}
      >
        {/* MODAL HEADER */}
        <div className="d-flex justify-content-between align-items-center mb-5">
           <div>
              <h2 className="fw-black text-white m-0 h1">SUBSCRIPTION <span className="text-primary">PLANS</span></h2>
              <p className="text-white-50 m-0 font-monospace small">SELECT SUBSCRIPTION PLAN</p>
           </div>
           <button className="btn btn-outline-light rounded-circle p-2 d-flex align-items-center justify-content-center" 
                   onClick={() => setShowPricingModal(false)} style={{width: 40, height: 40}}>
             <i className="bi bi-x-lg"></i>
           </button>
        </div>

        {/* PLANS GRID */}
        <div className="row g-4 justify-content-center">
          {plans.map((plan, index) => {
            const isFeatured = index === 1; // Middle plan featured
            return (
              <div className="col-xl-4 col-md-6" key={plan.id}>
                <motion.div 
                  whileHover={{ y: -10 }}
                  className={`h-100 rounded-4 p-4 p-xl-5 d-flex flex-column border transition-all ${
                    isFeatured 
                    ? 'bg-primary bg-opacity-10 border-primary shadow-primary' 
                    : 'bg-dark bg-opacity-5 border-white border-opacity-10'
                  }`}
                >
                  {/* Plan Badge */}
                  <div className="mb-4">
                    <span className={`badge rounded-pill mb-3 px-3 py-2 ${isFeatured ? 'bg-primary' : 'bg-white bg-opacity-10 text-white-50'}`}>
                      {isFeatured ? 'MOST_POPULAR' : 'STABLE_NODE'}
                    </span>
                    <h3 className="fw-black text-white mb-1">{plan.name}</h3>
                    <div className="d-flex align-items-baseline gap-1 mt-3">
                      <span className="h4 fw-bold text-primary mb-0">₹</span>
                      <span className="display-5 fw-black text-white mb-0">{plan.price}</span>
                      <span className="text-white-50 x-small font-monospace">/{plan.duration_days}D</span>
                    </div>
                  </div>

                  {/* Feature List (High-Tech Style) */}
                  <div className="flex-grow-1">
                    <div className="mb-4 py-3 border-top border-bottom border-white border-opacity-5">
                       <div className="d-flex justify-content-between mb-2 small">
                          <span className="text-white">DEVICES</span>
                          <span className="text-white fw-bold">{plan.device_limit} Devices</span>
                       </div>
                       <div className="stat-bar bg-dark" style={{height: 4}}>
                          <div className="bg-primary h-100" style={{width: `${(plan.device_limit/20)*100}%`}}></div>
                       </div>
                    </div>

                    <ul className="list-unstyled mb-0">
                      {[
                        { icon: 'bi-cpu', text: 'Cloud Node Hosting' },
                        { icon: 'bi-broadcast', text: `${plan.message_limit.toLocaleString()} Messages` },
                        { icon: 'bi-terminal-split', text: 'Webhook Access' },
                        { icon: 'bi-shield-check', text: 'API Access' }
                      ].map((feat, i) => (
                        <li key={i} className="mb-3 d-flex align-items-center gap-3">
                          <i className={`bi ${feat.icon} text-primary`}></i>
                          <span className="text-white small">{feat.text}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Action Button */}
                  <div className="mt-5">
                    <button 
                      className={`btn w-100 py-3 rounded-4 fw-black border-0 ${
                        isFeatured ? 'btn-primary shadow-sm' : 'btn-outline-light'
                      }`}
                      onClick={() => navigate('/register', { state: { plan } })}
                    >
                      INITIALIZE <i className="bi bi-chevron-right ms-1"></i>
                    </button>
                  </div>
                </motion.div>
              </div>
            );
          })}
        </div>

        {/* MODAL FOOTER SECURE TAG */}
        <div className="text-center mt-5 opacity-50">
           <span className="font-monospace extra-small">
             <i className="bi bi-lock-fill me-2"></i>
             SECURE_ENCRYPTED_PAYMENT_GATEWAY_V1.0
           </span>
        </div>
      </motion.div>
    </div>
  )}
</AnimatePresence>

    </PageTransition>
  );
}